import type { FC, ReactNode } from 'react'
import type { LayoutData } from '@/types/content'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { Search, X } from 'lucide-react'
import { cms } from '@/lib/cms'
import { cn } from '@/lib/utils'
import { Modal } from '@/components/kit/modal'
import { Wordmark } from '@/components/media/wordmark'
import { SocialLinks } from '@/components/site/social-links'
import { exploreNav, mainNav, romanNumerals } from '@/constants/site'

interface MobileMenuProps {
    open: boolean
    onClose: () => void
    onSearch: () => void
    layout: LayoutData
}

const isActive = (pathname: string, href: string): boolean => href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`)

export const MobileMenu: FC<MobileMenuProps> = ({ open, onClose, onSearch, layout }): ReactNode => {
    const router = useRouter()
    const path = router.asPath.split('?')[0]

    return (
        <Modal open={open} onClose={onClose} label='Site menu' className='h-dvh w-screen'>
            <div className='relative flex h-dvh w-screen flex-col overflow-y-auto bg-obsidian'>
                <div className='pointer-events-none absolute inset-0 opacity-60' aria-hidden='true'>
                    <div className='absolute -right-32 -top-32 size-96 rounded-full bg-burgundy/20 blur-3xl' />
                    <div className='absolute -bottom-40 -left-24 size-96 rounded-full bg-gold/10 blur-3xl' />
                </div>

                <div className='shell relative flex h-16 shrink-0 items-center justify-between'>
                    <Link href='/' onClick={onClose} aria-label='Serene Corpse, home'>
                        <Wordmark size='sm' />
                    </Link>

                    <button type='button' onClick={onClose} className='inline-flex size-11 items-center justify-center rounded-full border border-ivory/15 text-ivory transition hover:border-ivory/40' aria-label='Close menu'>
                        <X className='size-5' aria-hidden='true' />
                    </button>
                </div>

                <nav aria-label='Main' className='shell relative flex-1 py-8'>
                    <ul className='flex flex-col gap-1'>
                        {mainNav.map((item, index) => (
                            <li key={item.href} className='animate-in fade-in slide-in-from-bottom-3 fill-mode-both duration-500' style={{ animationDelay: `${index * 45}ms` }}>
                                <Link
                                    href={item.href}
                                    onClick={onClose}
                                    aria-current={isActive(path, item.href) ? 'page' : undefined}
                                    className={cn(
                                        'group flex items-baseline gap-4 py-2 font-heading text-[2.1rem] font-light leading-tight transition-colors sm:text-5xl',
                                        isActive(path, item.href) ? 'text-gold' : 'text-ivory/85 hover:text-ivory'
                                    )}
                                >
                                    <span className='w-9 font-brand text-[0.65rem] tracking-[0.2em] text-ivory/35'>
                                        {romanNumerals[index]}
                                    </span>
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <div className='mt-10 border-t border-white/[0.07] pt-8'>
                        <p className='eyebrow mb-4 text-ivory/40'>
                            Explore
                        </p>

                        <ul className='grid grid-cols-2 gap-x-6 gap-y-3'>
                            {exploreNav.map(item => (
                                <li key={item.href}>
                                    <Link href={item.href} onClick={onClose} className='text-sm text-ivory/70 transition hover:text-ivory'>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </nav>

                <div className='shell relative flex flex-col gap-6 pb-10'>
                    <button
                        type='button'
                        onClick={() => {
                            onClose()
                            onSearch()
                        }}
                        className='flex h-12 items-center gap-3 rounded-full border border-ivory/15 px-5 text-sm text-ivory/70 transition hover:border-ivory/40 hover:text-ivory'
                    >
                        <Search className='size-4' aria-hidden='true' />
                        Search songs, lyrics and writings
                    </button>

                    <SocialLinks socials={layout.artist} />

                    {layout.artist.tagline && (
                        <p className='font-heading text-xl italic text-ivory/60'>
                            <span {...cms('artist', layout.artist.id, 'tagline')}>{layout.artist.tagline}</span>
                        </p>
                    )}
                </div>
            </div>
        </Modal>
    )
}
